<template>
  <section class="rule-card" :class="{ 'rule-card--locked': isLocked, 'rule-card--cycle': isInCycle }" :style="accentVars">
    <header class="rule-card__header" @click="handleCardClick">
      <div class="rule-card__header-main">
        <div class="feature-title">
          <span class="feature-icon">
            <component
              :is="tabMeta.iconComponent"
              :color="tabMeta.color"
              width="18px"
              height="18px"
            />
          </span>
          <span class="eyebrow">WHEN FEATURE</span>
          <span class="dash">=</span>
          <span class="feature-pill">{{ rule.feature }}</span>
          <template v-if="isEditing">
            <input
              v-model="rule.name"
              :class="['rule-name-input', { 'rule-name-input--error': hasRuleNameError }]"
              type="text"
              placeholder="Enter Rule Name"
              @input="handleRuleNameInput"
              @click.stop
            >
          </template>
          <span v-else-if="rule.name" class="name-pill">{{ rule.name }}</span>
          <span class="summary">{{ summaryText }}</span>
        </div>

        <div class="card-actions-wrapper">
          <div class="card-actions">
            <span v-if="isInCycle" class="cycle-badge" v-tooltip="'This rule is part of a circular dependency'">&#9888; Cycle</span>
            <span v-if="canManageRules && !isEditing" class="status" :class="{ inactive: !rule.active, disabled: isLocked }" @click="toggleActive">
              <span class="status-dot"></span>
              {{ rule.active ? 'Active' : 'Inactive' }}
            </span>
            <template v-if="canManageRules && isEditing">
              <button class="icon-btn edit-close-action" type="button" @click="cancelEdit">
                <img src="~@/assets/images/close.png" alt="Cancel edit" width="14px">
              </button>
              <button class="icon-btn edit-save-action" type="button" @click="saveEdit">
                <img src="~@/assets/images/icons/save-icon.svg" alt="Save rule" width="14px">
              </button>
            </template>
            <button
              v-else-if="canManageRules && !rule.isDraft"
              class="icon-btn edit-action"
              type="button"
              :disabled="isLocked"
              @click="startEdit"
            >
              <img src="~@/assets/images/pencil.svg" alt="Edit" width="14px">
            </button>
            <button
              v-if="canManageRules && !rule.isDraft"
              class="icon-btn delete-action"
              type="button"
              :disabled="isLocked"
              @click="openDeleteConfirm"
            >
              <delete-icon />
            </button>
            <button class="icon-btn rule-expand-toggle" type="button" @click="toggleExpanded">
              <chevron-icon
                class="chevron"
                :direction="rule.expanded ? 'up' : 'down'"
              />
            </button>
          </div>
        </div>
      </div>
      <div v-if="validationMessage" class="rule-card__header-error-row">
        <p class="rule-card__error-message rule-card__error-message--header">
          {{ validationMessage }}
        </p>
      </div>
    </header>

    <div v-show="rule.expanded" class="rule-card__body" :class="{ 'rule-card__body--readonly': isReadOnly }">
      <section class="configure-section">
        <div class="section-heading">
          <configure-icon
            class="section-symbol configure-symbol"
            color="#c9852f"
            width="18px"
            height="18px"
          />
          <h4>CONFIGURE FOR</h4>
          <div class="configure-dropdown">
            <custom-dropdown
              :list="lists.configurationTypes"
              :selected="configurationTypeOption(rule.configuration)"
              :default-style="configTypeStyle"
              prevent-deselection
              @select="requestConfigurationChange"
            />
          </div>
        </div>
      </section>

      <section class="condition-set">
        <div class="condition-set__body">
          <section :key="configurationKey" class="rule-section conditions-section">
            <div
              class="conditions-layout"
              :class="{ 'conditions-layout--preview-collapsed': rule.previewExpanded === false }"
            >
              <div class="conditions-list">
                <div class="if-heading-row">
                  <div class="section-heading column">
                  <div>
                    <h4>IF CONDITIONS</h4>
                    <p>Define when this rule should trigger</p>
                  </div>
                  </div>
                  <div class="section-heading-actions">
                    <span v-if="showAddGroupError && hasIncompleteConditionsInRule" class="inline-error">Complete current conditions first.</span>
                    <button
                      v-if="canManageRules"
                      class="text-btn add-group-btn"
                      type="button"
                      @click="addGroup"
                    >
                      + Add Group
                    </button>
                  </div>
                </div>

                <template v-for="(group, groupIndex) in conditionGroups">
                  <condition-group
                    :key="group.id"
                    :group="group"
                    :index="groupIndex"
                    :all-groups="conditionGroups"
                    :lists="lists"
                    :selectedTab="selectedTab"
                    :selectedConfig="rule.configuration"
                    :validation="conditionValidationMap"
                    :can-manage-rules="canManageRules"
                    @update="updateGroup"
                    @request-match-change="requestGroupMatchChange"
                    @remove="removeGroup"
                  />
                  <div
                    v-if="groupIndex < conditionGroups.length - 1"
                    :key="`${group.id}-operator`"
                    :class="['between-operator-row', operatorClass(group.joinWithNext)]"
                  >
                    <button
                      v-if="canManageRules"
                      class="between-operator"
                      type="button"
                      @click="toggleGroupJoin(group.id)"
                    >
                      {{ group.joinWithNext || 'AND' }}
                    </button>
                    <span v-else class="between-operator">
                      {{ group.joinWithNext || 'AND' }}
                    </span>
                  </div>
                </template>

                <div v-if="!conditionGroups.length" class="empty-state empty-conditions">
                  No conditions — rule applies to all records
                </div>
              </div>

              <aside
                class="rule-preview"
                :class="{ 'rule-preview--collapsed': rule.previewExpanded === false }"
              >
                <div v-if="rule.previewExpanded === false" class="collapsed-preview">
                  <img
                    v-tooltip="'Expand Rule Preview'"
                    class="preview-toggle-icon preview-toggle-control"
                    @click="togglePreview"
                    src="~@/assets/images/icons/collapse-sidebar.svg"
                    width="13px"
                    alt=""
                  />
                  <div class="preview-divider" />
                  <div class="preview-vertical-text">
                    <span>Rule preview</span>
                  </div>
                </div>
                <template v-else>
                  <button
                    class="preview-title preview-toggle-control"
                    type="button"
                    @click="togglePreview"
                  >
                    <img
                      v-tooltip="'Collapse Rule Preview'"
                      class="preview-toggle-icon"
                      src="~@/assets/images/icons/expand-sidebar.svg"
                      width="13px"
                      alt=""
                    />
                    <span>Rule preview</span>
                  </button>
                  <p v-if="!conditionGroups.length" class="empty-preview">
                    No conditions yet
                  </p>
                  <template v-for="(group, groupIndex) in conditionGroups">
                  <div
                    :key="`${group.id}-preview`"
                    class="preview-group"
                    :class="`preview-${(groupIndex % 6) + 1}`"
                  >
                    <strong>GROUP {{ groupIndex + 1 }} <span>({{ group.match }})</span></strong>
                    <p
                      v-for="condition in group.conditions"
                      :key="`${condition.id}-preview-line`"
                    >
                      {{ displayOption(condition.field) }} {{ displayOption(condition.operator) }} {{ displayOption(condition.value) || 'value...' }}
                    </p>
                  </div>
                  <div
                    v-if="groupIndex < conditionGroups.length - 1"
                    :key="`${group.id}-preview-operator`"
                    :class="['preview-operator-row', operatorClass(group.joinWithNext)]"
                  >
                    <span class="preview-operator">{{ group.joinWithNext || 'AND' }}</span>
                  </div>
                  </template>
                </template>
              </aside>
            </div>
          </section>

          <section class="rule-section then-section">
            <div class="section-heading split">
              <div class="section-heading-content">
                <flash-icon
                  class="section-symbol then-symbol"
                  color="#12b76a"
                  width="15px"
                  height="15px"
                />
                <div>
                  <h4>THEN ACTIONS <span>{{ actions.length }}</span></h4>
                  <p>Actions to perform when the above conditions are met</p>
                </div>
              </div>
            </div>

            <div v-if="isSequenceTemplateActionFlow" class="action-list">
              <action-row
                v-for="action in actions"
                :key="action.id"
                :action="action"
                :actions="actions"
                :lists="lists"
                :selected-tab="selectedTab"
                :selected-config="rule.configuration"
                :show-delete-action="canManageRules && canRemoveActionRows"
                :validation="getActionValidation(action)"
                @update="updateAction"
                @remove="removeAction"
              />

              <section v-if="actions.length > 0" class="mandatory-checks">
                <div
                  class="add-mandatory-checks"
                  @click="showMandatoryChecks = !showMandatoryChecks"
                >
                  {{ rule.value }}
                  <span v-if="!showMandatoryChecks" class="icon">+</span>
                  <chevron-icon v-if="showMandatoryChecks"
                    class="chevron"
                    direction="up"
                  />
                  <span>Add Mandatory Checks</span>
                </div>
                <transition name="slide-fade">
                  <div
                    v-if="showMandatoryChecks"
                    class="mandatory-checks-container"
                  >
                    <mandatory-checks
                      v-for="(group, index) in mandatoryCheckGroups"
                      :key="group.fieldGroup.type"
                      :field-group="group.fieldGroup"
                      :selected-fields="group.selectedFields"
                      :is-last="index === mandatoryCheckGroups.length - 1"
                      @update-fields="updateMandatoryFields"
                    />
                  </div>
                </transition>
              </section>
            </div>

            <template v-else>
              <div v-if="shouldShowSetActions" class="action-group">
                <div class="action-group__header">
                  <div>
                    <h5>Set Group</h5>
                    <p>Directly assign values to target fields.</p>
                  </div>
                  <div class="section-heading-actions">
                    <button
                      v-if="canManageRules"
                      class="text-btn green"
                      :class="{ 'text-btn--disabled': isAddSetDisabled }"
                      :disabled="isSetAtCapacity"
                      type="button"
                      @click="addAction('SET')"
                    >
                      + Add Set
                    </button>
                  </div>
                </div>
                <div class="action-list">
                  <action-row
                    v-for="action in setActions"
                    :key="action.id"
                    :action="action"
                    :actions="actions"
                    :lists="lists"
                    :selected-tab="selectedTab"
                    :selected-config="rule.configuration"
                    :show-delete-action="canManageRules && canRemoveSetActionRows"
                    :validation="getActionValidation(action)"
                    :add-attempt-error="showAddSetError && incompleteSetActionIds.includes(action.id)"
                    @update="updateAction"
                    @remove="removeAction"
                  />
                </div>

              </div>

              <div class="action-group">
                <div class="action-group__header">
                  <div>
                    <h5>Map Group</h5>
                    <p>Map target fields from a selected source and source key.</p>
                  </div>
                  <div class="section-heading-actions">
                    <button
                      v-if="canManageRules"
                      class="text-btn green"
                      :class="{ 'text-btn--disabled': isAddMapDisabled }"
                      :disabled="isMapAtCapacity"
                      type="button"
                      @click="addAction('MAP')"
                    >
                      + Add Map
                    </button>
                  </div>
                </div>
                <div class="action-list">
                  <map-action-row
                    v-for="action in mapActions"
                    :key="action.id"
                    :action="action"
                    :actions="actions"
                    :lists="lists"
                    :selected-tab="selectedTab"
                    :selected-config="rule.configuration"
                    :show-delete-action="canManageRules && canRemoveActionRows"
                    :validation="getActionValidation(action)"
                    :add-attempt-error="showAddMapError && incompleteMapActionIds.includes(action.id)"
                    @update="updateAction"
                    @remove="removeAction"
                  />
                </div>
                <div v-if="!mapActions.length" class="empty-state empty-actions empty-actions--group">
                  No map actions configured
                </div>
              </div>
            </template>

            <div v-if="isSequenceTemplateActionFlow && !actions.length" class="empty-state empty-actions">
              No actions configured
            </div>
          </section>
        </div>
      </section>

    </div>
    <modal
      :open="showDeleteConfirmModal"
      title="Delete Rule?"
      :scroll="false"
      @close="closeDeleteConfirm"
    >
      <div class="configuration-confirmation">
        <p class="configuration-confirmation__message">
          This rule will be permanently deleted. Do you want to delete?
        </p>
        <div class="configuration-confirmation__actions">
          <button type="button" class="btn btn-secondary" @click="closeDeleteConfirm">Cancel</button>
          <button type="button" class="btn" @click="confirmRemove">Confirm</button>
        </div>
      </div>
    </modal>
    <modal
      :open="showConfigurationConfirmModal"
      title="Change Configure For?"
      :scroll="false"
      @close="closeConfigurationConfirmModal"
    >
      <div class="configuration-confirmation">
        <p class="configuration-confirmation__message">
          Changing the Feature will remove all the condition rows. Are you sure you want to change the feature?
        </p>
        <div class="configuration-confirmation__actions">
          <button type="button" class="btn btn-secondary" @click="closeConfigurationConfirmModal">Cancel</button>
          <button type="button" class="btn" @click="confirmConfigurationChange">Change Feature</button>
        </div>
      </div>
    </modal>
    <modal
      :open="showJoinConflictModal"
      title="Duplicate Fields Found"
      :scroll="false"
      @close="closeJoinConflictModal"
    >
      <div class="configuration-confirmation">
        <p class="configuration-confirmation__message">
          The following fields are repeated across these groups: {{ pendingJoinConflictLabels }}. If you switch to AND, duplicate fields will only be kept in the latest condition group.
        </p>
        <div class="configuration-confirmation__actions">
          <button type="button" class="btn btn-secondary" @click="closeJoinConflictModal">Cancel</button>
          <button type="button" class="btn" @click="confirmJoinConflictResolution">Confirm</button>
        </div>
      </div>
    </modal>
    <modal
      :open="showGroupMatchConflictModal"
      title="Duplicate Fields Detected"
      :scroll="false"
      @close="closeGroupMatchConflictModal"
    >
      <div class="configuration-confirmation">
        <p class="configuration-confirmation__message">
          The following fields are repeated in this group: {{ pendingGroupMatchConflictLabels }}. If you switch to AND, only the latest row for each duplicate field will be kept, and the rest will be removed.
        </p>
        <div class="configuration-confirmation__actions">
          <button type="button" class="btn btn-secondary" @click="closeGroupMatchConflictModal">Cancel</button>
          <button type="button" class="btn" @click="confirmGroupMatchConflictResolution">Confirm</button>
        </div>
      </div>
    </modal>
    <modal
      :open="showRemoveSetActionsModal"
      title="Remove Set Actions?"
      :scroll="false"
      @close="closeRemoveSetActionsModal"
    >
      <div class="configuration-confirmation">
        <p class="configuration-confirmation__message">
          Deleting all 'IF' conditions will also remove all configured 'SET' actions. Do you want to proceed?
        </p>
        <div class="configuration-confirmation__actions">
          <button type="button" class="btn btn-secondary" @click="closeRemoveSetActionsModal">Cancel</button>
          <button type="button" class="btn" @click="confirmRemoveSetActions">Proceed</button>
        </div>
      </div>
    </modal>
  </section>
</template>

<script>
import CustomDropdown from '@/components/common/customDropdown.vue'
import DeleteIcon from '@/components/common/icons/deleteIcon.vue'
import ChevronIcon from '@/components/common/icons/chevronIcon.vue'
import FlashIcon from '@/components/common/icons/flashIcon.vue'
import ConfigureIcon from '@/components/common/icons/configureIcon.vue'
import Modal from '@/components/common/modal.vue'
import ConditionGroup from './ConditionGroup.vue'
import ActionRow from './ActionRow.vue'
import MandatoryChecks from './mandatoryChecks.vue'
import MapActionRow from './MapActionRow.vue'
import { clone, createAction, createConditionGroup } from './configModel'
import { buildRuleStateForConfiguration, dedupeConditionGroupsForAndMerge, dedupeConditionsForAndGroup, fetchSourceItems, findOptionByValue, getActionTargetItems, getActionValidationState, getAndConnectedGroupIndexes, getConditionValidationState, getConfigurationChangeConflicts, getDuplicateFieldsAcrossGroups, getDuplicateFieldsInGroup, getMaterialCondtions, hasConditionRows, isSequenceTemplateConfig, normalizeActionsForConfiguration, optionLabel, optionValue, pruneEmptyConditionGroups, withValidationStyle } from './conditionHelper'
import config from '@/config'

export default {
  name: 'ConfigurationRuleCard',
  components: {
    CustomDropdown,
    DeleteIcon,
    ChevronIcon,
    FlashIcon,
    ConfigureIcon,
    Modal,
    ConditionGroup,
    ActionRow,
    MapActionRow,
    MandatoryChecks
  },
  watch: {
    mandatoryCheckGroups: {
      immediate: true,
      handler (groups) {
        this.localMandatoryGroupdata = clone(groups)
      }
    },
    hasIncompleteConditionsInRule (nextValue) {
      if (!nextValue) {
        this.showAddGroupError = false
      }
    },
    hasIncompleteSetActions (nextValue) {
      if (!nextValue) {
        this.showAddSetError = false
      }
    },
    hasIncompleteMapActions (nextValue) {
      if (!nextValue) {
        this.showAddMapError = false
      }
    }
  },
  props: {
    rule: {
      type: Object,
      required: true
    },
    tabMeta: {
      type: Object,
      required: true
    },
    lists: {
      type: Object,
      required: true
    },
    validation: {
      type: Object,
      default: () => ({})
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    isLocked: {
      type: Boolean,
      default: false
    },
    isInCycle: {
      type: Boolean,
      default: false
    },
    canManageRules: {
      type: Boolean,
      default: false
    },
    selectedTab: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      showDeleteConfirmModal: false,
      pendingConfiguration: null,
      showConfigurationConfirmModal: false,
      pendingJoinConflict: null,
      showJoinConflictModal: false,
      pendingGroupMatchConflict: null,
      showGroupMatchConflictModal: false,
      showMandatoryChecks: false,
      localMandatoryGroupdata: [],
      pendingRemoveSetActionsGroupId: null,
      showRemoveSetActionsModal: false,
      showAddGroupError: false,
      showAddSetError: false,
      showAddMapError: false,
      dropdownStyles: {
        configType: {
          dropDownWidth: '250px',
          borderColor: '#f1c989',
          backGroundColor: '#fffaf0',
          labelHeight: '30px',
          ddLabelFontSize: 'var(--text-xs2)'
        }
      }
    }
  },
  computed: {
    accentVars () {
      return {
        '--accent-color': this.tabMeta.color,
        '--accent-soft': this.tabMeta.softColor
      }
    },
    conditionGroups () {
      return this.rule.conditionGroups
    },
    actions () {
      return this.rule.actions
    },
    configTypeStyle () {
      return withValidationStyle(this.dropdownStyles.configType, this.validation.configuration)
    },
    isReadOnly () {
      return !this.isEditing && !this.rule.isDraft
    },
    canAddAction () {
      return !isSequenceTemplateConfig(this.rule.configuration)
    },
    availableConditionSources () {
      return fetchSourceItems({
        selectedTab: this.selectedTab.name,
        selectedConfig: this.rule.configuration,
        sourceFieldTypes: this.lists.sourceFieldTypes
      })
    },
    incompleteConditionIds () {
      return this.conditionGroups.flatMap(group => {
        return (group.conditions || [])
          .filter(condition => Object.values(getConditionValidationState(condition)).some(Boolean))
          .map(condition => condition.id)
      })
    },
    hasIncompleteConditionsInRule () {
      return this.incompleteConditionIds.length > 0
    },
    canRemoveActionRows () {
      return !isSequenceTemplateConfig(this.rule.configuration) && this.actions.length > 1
    },
    hasIfConditions () {
      return hasConditionRows(this.rule)
    },
    isSequenceTemplateActionFlow () {
      return isSequenceTemplateConfig(this.rule.configuration)
    },
    shouldShowSetActions () {
      return !this.isSequenceTemplateActionFlow && this.hasIfConditions
    },
    setActions () {
      return this.actions.filter(action => optionValue(action.type) !== 'MAP')
    },
    mapActions () {
      return this.actions.filter(action => optionValue(action.type) === 'MAP')
    },
    availableSetFieldCapacity () {
      const usedFieldIds = new Set(this.setActions.map(action => optionValue(action.field)).filter(Boolean))
      const availableTargets = getActionTargetItems({
        selectedTab: this.selectedTab.name,
        selectedConfig: this.rule.configuration,
        actions: this.actions,
        actionType: 'SET'
      }).filter(item => {
        return !(optionValue(this.rule.configuration) === 'STANDARD_MATERIAL_FORM' && item?.type === config.FORM_TYPE.TABLE && item?.type === config.FORM_TYPE.DATE && item?.type === config.FORM_TYPE.TIME) &&
          !this.isErpIdField(item)
      })

      return usedFieldIds.size + availableTargets.length
    },
    availableMapFieldCapacity () {
      const usedFieldIds = new Set(this.mapActions.map(action => optionValue(action.field)).filter(Boolean))
      const availableTargets = getActionTargetItems({
        selectedTab: this.selectedTab.name,
        selectedConfig: this.rule.configuration,
        actions: this.actions,
        actionType: 'MAP'
      }).filter(item => {
        return !(optionValue(this.rule.configuration) === 'STANDARD_MATERIAL_FORM' && item?.type === config.FORM_TYPE.TABLE && item?.type === config.FORM_TYPE.DATE && item?.type === config.FORM_TYPE.TIME) &&
          !this.isErpIdField(item)
      })

      return usedFieldIds.size + availableTargets.length
    },
    incompleteSetActionIds () {
      return this.setActions
        .filter(action => Object.values(getActionValidationState(action, this.rule.configuration)).some(Boolean))
        .map(action => action.id)
    },
    incompleteMapActionIds () {
      return this.mapActions
        .filter(action => Object.values(getActionValidationState(action, this.rule.configuration)).some(Boolean))
        .map(action => action.id)
    },
    hasIncompleteSetActions () {
      return this.incompleteSetActionIds.length > 0
    },
    hasIncompleteMapActions () {
      return this.incompleteMapActionIds.length > 0
    },
    isSetAtCapacity () {
      return this.availableSetFieldCapacity <= 0 || this.setActions.length >= this.availableSetFieldCapacity
    },
    isMapAtCapacity () {
      return this.availableMapFieldCapacity <= 0 || this.mapActions.length >= this.availableMapFieldCapacity
    },
    isAddSetDisabled () {
      return this.isSetAtCapacity
    },
    isAddMapDisabled () {
      return this.isMapAtCapacity
    },
    canRemoveSetActionRows () {
      return this.shouldShowSetActions && this.setActions.length > 1
    },
    summaryText () {
      return `1 config - ${this.conditionGroups.length} groups - ${this.actions.length} actions`
    },
    configurationKey () {
      return optionValue(this.rule.configuration) || 'no-config'
    },
    pendingGroupMatchConflictLabels () {
      if (!this.pendingGroupMatchConflict || !this.pendingGroupMatchConflict.duplicates.length) {
        return ''
      }

      return this.pendingGroupMatchConflict.duplicates
        .map(item => item.label)
        .filter(Boolean)
        .join(', ')
    },
    pendingJoinConflictLabels () {
      if (!this.pendingJoinConflict || !this.pendingJoinConflict.duplicates.length) {
        return ''
      }

      return this.pendingJoinConflict.duplicates
        .map(item => item.label)
        .filter(Boolean)
        .join(', ')
    },
    mandatoryCheckGroups () {
      const metadata = this.rule.actions?.[0]?.metadata || {}

      const materialOptions = getMaterialCondtions({ selectedSource: 'MATERIAL_FIXED_FIELDS' }) || []
      const smfOptions = getMaterialCondtions({ selectedSource: 'STANDARD_MATERIAL_FORM' }) || []

      const formatOption = (opt) => ({
        name: opt.name || opt.id,
        label: opt.name || opt.id,
        id: opt.id,
        value: opt.id,
        type: opt.type
      })

      const groups = []

      // MATERIAL_FIXED_FIELDS group
      const mfSelectedIds = metadata.MATERIAL_FIXED_FIELDS || []
      groups.push({
        fieldGroup: {
          label: 'Material Fixed Fields',
          type: 'MATERIAL_FIXED_FIELDS',
          options: materialOptions.map(formatOption)
        },
        selectedFields: mfSelectedIds
          .map(id => materialOptions.find(opt => opt.id === id))
          .filter(Boolean)
          .map(formatOption)
      })

      // STANDARD_MATERIAL_FORM group
      const smfSelectedIds = metadata.STANDARD_MATERIAL_FORM || []
      groups.push({
        fieldGroup: {
          label: 'Standard Form Fields',
          type: 'STANDARD_MATERIAL_FORM',
          options: smfOptions.map(formatOption)
        },
        selectedFields: smfSelectedIds
          .map(id => smfOptions.find(opt => opt.id === id))
          .filter(Boolean)
          .map(formatOption)
      })

      return groups
    },
    validationMessage () {
      return this.validation?.message || ''
    },
    conditionValidationMap () {
      const baseValidation = { ...((this.validation && this.validation.conditions) || {}) }

      if (this.showAddGroupError || this.hasIncompleteConditionsInRule) {
        this.conditionGroups.forEach((group) => {
          ;(group.conditions || []).forEach((condition) => {
            const nextValidation = getConditionValidationState(condition)
            if (Object.values(nextValidation).some(Boolean)) {
              baseValidation[condition.id] = {
                ...(baseValidation[condition.id] || {}),
                ...nextValidation
              }
            }
          })
        })
      }

      return baseValidation
    },
    hasRuleNameError () {
      return Boolean(this.validation?.nameRequired || this.validation?.duplicateName)
    },
    hasDuplicateConditionsError () {
      return Boolean(this.validation?.duplicateConditions)
    }
  },
  methods: {
    isErpIdField (item) {
      const normalizedLabel = this.displayOption(item)?.trim()?.toLowerCase()
      return normalizedLabel === 'erp material id'
    },
    handleCardClick (event) {
      if (event.target.closest('button, input, textarea, select, a, .status')) {
        return
      }

      this.toggleExpanded()
    },
    updateMandatoryFields ({ type, value }) {
      const actions = clone(this.actions)

      if (!actions.length) return

      const metadata = actions[0].metadata ? clone(actions[0].metadata) : {}

      metadata[type] = value
      actions[0].metadata = metadata

      this.updateRuleData({ actions })
    },
    toOption (value) {
      if (!value) return {}
      if (typeof value === 'object') return value
      return { id: value, name: value }
    },
    configurationTypeOption (value) {
      return findOptionByValue(this.lists.configurationTypes, value) || this.toOption(value)
    },
    displayOption (value) {
      return optionLabel(value)
    },
    getActionValidation (action) {
      const baseValidation = {
        ...(((this.validation && this.validation.actions) || {})[action.id] || {})
      }
      const isSetAction = optionValue(action?.type) !== 'MAP'
      const shouldShowLocalValidation = isSetAction ? this.showAddSetError : this.showAddMapError

      if (!shouldShowLocalValidation) {
        return baseValidation
      }

      const nextValidation = getActionValidationState(action, this.rule.configuration)
      return {
        ...baseValidation,
        ...nextValidation
      }
    },
    update (patch) {
      this.$emit('update', { ...clone(this.rule), ...patch })
    },
    updateRuleData (patch) {
      const next = clone(this.rule)
      Object.assign(next, patch)
      this.$emit('update', next)
    },
    closeConfigurationConfirmModal () {
      this.showConfigurationConfirmModal = false
      this.pendingConfiguration = null
    },
    closeJoinConflictModal () {
      this.showJoinConflictModal = false
      this.pendingJoinConflict = null
    },
    closeGroupMatchConflictModal () {
      this.showGroupMatchConflictModal = false
      this.pendingGroupMatchConflict = null
    },
    closeRemoveSetActionsModal () {
      this.showRemoveSetActionsModal = false
      this.pendingRemoveSetActionsGroupId = null
    },
    openDeleteConfirm () {
      if (this.isLocked) {
        return
      }
      this.showDeleteConfirmModal = true
    },
    closeDeleteConfirm () {
      this.showDeleteConfirmModal = false
    },
    confirmRemove () {
      this.$emit('remove', this.rule.id)
      this.closeDeleteConfirm()
    },
    startEdit () {
      if (this.isLocked) {
        return
      }
      this.$emit('start-edit', this.rule.id)
    },
    saveEdit () {
      this.$emit('save-edit', clone(this.rule))
    },
    handleRuleNameInput () {
      this.updateRuleData({ name: this.rule.name })
    },
    cancelEdit () {
      this.$emit('cancel-edit', this.rule.id)
    },
    toggleActive () {
      if (this.isLocked) {
        return
      }
      this.update({ active: !this.rule.active })
    },
    toggleExpanded () {
      this.update({ expanded: !this.rule.expanded })
    },
    requestConfigurationChange (value) {
      const nextConfigId = optionValue(value)
      if (nextConfigId === optionValue(this.rule.configuration)) {
        return
      }

      const conflicts = getConfigurationChangeConflicts({
        selectedTab: this.selectedTab.name,
        selectedConfig: this.rule.configuration,
        nextConfig: value,
        sourceFieldTypes: this.lists.sourceFieldTypes,
        conditionGroups: this.conditionGroups
      })

      if (!conflicts.length) {
        this.updateRuleData({
          configuration: value,
          actions: normalizeActionsForConfiguration(value, this.actions, this.rule.configuration)
        })
        return
      }

      this.pendingConfiguration = value
      this.showConfigurationConfirmModal = true
    },
    confirmConfigurationChange () {
      if (!this.pendingConfiguration) {
        return
      }
      this.$emit('update', buildRuleStateForConfiguration(this.rule, this.pendingConfiguration, 1))
      this.closeConfigurationConfirmModal()
    },
    togglePreview () {
      this.updateRuleData({ previewExpanded: this.rule.previewExpanded === false })
    },
    addGroup () {
      this.showAddGroupError = true
      if (this.hasIncompleteConditionsInRule) {
        return
      }

      const hadNoConditions = !this.hasIfConditions
      const nextGroups = clone(this.conditionGroups)
      const nextActions = clone(this.actions)

      if (nextGroups.length) {
        nextGroups[nextGroups.length - 1].joinWithNext = 'OR'
      }

      nextGroups.push(createConditionGroup())
      if (hadNoConditions && !this.setActions.length) {
        nextActions.push(createAction('SET'))
      }
      this.showAddGroupError = false
      this.updateRuleData({ conditionGroups: nextGroups, actions: nextActions })
    },
    updateGroup (updatedGroup) {
      const groups = clone(this.conditionGroups)
      const index = groups.findIndex(group => group.id === updatedGroup.id)
      if (index >= 0) groups.splice(index, 1, updatedGroup)
      this.updateRuleData({ conditionGroups: groups })
    },
    requestGroupMatchChange ({ groupId, nextMatch }) {
      const groups = clone(this.conditionGroups)
      const groupIndex = groups.findIndex(group => group.id === groupId)
      const group = groups[groupIndex]

      if (!group || !nextMatch || nextMatch === group.match) {
        return
      }

      if (nextMatch === 'OR') {
        group.match = 'OR'
        this.updateRuleData({ conditionGroups: groups })
        return
      }

      const duplicates = getDuplicateFieldsInGroup(group)
      if (!duplicates.length) {
        group.match = 'AND'
        this.updateRuleData({ conditionGroups: groups })
        return
      }

      this.pendingGroupMatchConflict = {
        groupId,
        duplicates
      }
      this.showGroupMatchConflictModal = true
    },
    confirmGroupMatchConflictResolution () {
      if (!this.pendingGroupMatchConflict) {
        return
      }

      const groups = clone(this.conditionGroups)
      const groupIndex = groups.findIndex(group => group.id === this.pendingGroupMatchConflict.groupId)
      if (groupIndex < 0) {
        this.closeGroupMatchConflictModal()
        return
      }

      const nextGroup = dedupeConditionsForAndGroup(groups[groupIndex])
      nextGroup.match = 'AND'
      groups.splice(groupIndex, 1, nextGroup)
      this.updateRuleData({ conditionGroups: groups })
      this.closeGroupMatchConflictModal()
    },
    toggleGroupJoin (groupId) {
      const groups = clone(this.conditionGroups)
      const groupIndex = groups.findIndex(item => item.id === groupId)
      const group = groups[groupIndex]
      if (!group) {
        return
      }

      if (group.joinWithNext === 'AND') {
        group.joinWithNext = 'OR'
        this.updateRuleData({ conditionGroups: groups })
        return
      }

      const leftIndexes = getAndConnectedGroupIndexes(groups, groupIndex)
      const rightIndexes = getAndConnectedGroupIndexes(groups, groupIndex + 1)
      const duplicates = getDuplicateFieldsAcrossGroups({
        conditionGroups: groups,
        leftIndexes,
        rightIndexes
      })

      if (!duplicates.length) {
        group.joinWithNext = 'AND'
        this.updateRuleData({ conditionGroups: groups })
        return
      }

      this.pendingJoinConflict = {
        groupId,
        duplicates,
        leftIndexes,
        rightIndexes
      }
      this.showJoinConflictModal = true
    },
    confirmJoinConflictResolution () {
      if (!this.pendingJoinConflict) {
        return
      }

      const groups = clone(this.conditionGroups)
      const groupIndex = groups.findIndex(item => item.id === this.pendingJoinConflict.groupId)
      if (groupIndex < 0) {
        this.closeJoinConflictModal()
        return
      }

      const mergedIndexes = [...new Set([...this.pendingJoinConflict.leftIndexes, ...this.pendingJoinConflict.rightIndexes])].sort((a, b) => a - b)
      const dedupedGroups = dedupeConditionGroupsForAndMerge({
        conditionGroups: groups,
        mergedIndexes
      })
      if (dedupedGroups[groupIndex]) {
        dedupedGroups[groupIndex].joinWithNext = 'AND'
      }

      this.updateRuleData({
        conditionGroups: pruneEmptyConditionGroups(dedupedGroups)
      })
      this.closeJoinConflictModal()
    },
    operatorClass (operator) {
      return (operator || 'AND').toLowerCase() === 'or' ? 'operator-or' : 'operator-and'
    },
    removeGroup (groupId) {
      const nextGroups = clone(this.conditionGroups).filter(group => group.id !== groupId)
      const willRemoveAllConditions = !hasConditionRows({ conditionGroups: nextGroups })

      if (willRemoveAllConditions && this.setActions.length) {
        this.pendingRemoveSetActionsGroupId = groupId
        this.showRemoveSetActionsModal = true
        return
      }

      this.updateRuleData({
        conditionGroups: nextGroups
      })
    },
    confirmRemoveSetActions () {
      if (!this.pendingRemoveSetActionsGroupId) {
        return
      }

      this.updateRuleData({
        conditionGroups: clone(this.conditionGroups).filter(group => group.id !== this.pendingRemoveSetActionsGroupId),
        actions: clone(this.actions).filter(action => optionValue(action.type) === 'MAP')
      })
      this.closeRemoveSetActionsModal()
    },
    updateAction (updatedAction) {
      const actions = clone(this.actions)
      const index = actions.findIndex(item => item.id === updatedAction.id)
      if (index >= 0) actions.splice(index, 1, updatedAction)
      this.updateRuleData({ actions })
    },
    addAction (type = 'SET') {
      if (!this.canAddAction) {
        return
      }
      if (type === 'SET') {
        this.showAddSetError = true
        if (this.hasIncompleteSetActions || this.isSetAtCapacity) {
          return
        }
      }
      if (type === 'MAP') {
        this.showAddMapError = true
        if (this.hasIncompleteMapActions || this.isMapAtCapacity) {
          return
        }
      }
      if (type === 'SET') {
        this.showAddSetError = false
      }
      if (type === 'MAP') {
        this.showAddMapError = false
      }
      this.updateRuleData({ actions: [...clone(this.actions), createAction(type)] })
    },
    removeAction (actionId) {
      if (!this.canRemoveActionRows) {
        return
      }
      this.updateRuleData({
        actions: clone(this.actions).filter(item => item.id !== actionId)
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.rule-card {
  overflow: hidden;
  border: 1px solid #d9e0ea;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}

.rule-card--cycle {
  border-color: #fecdca;
  box-shadow: 0 0 0 1px #fecdca, 0 1px 2px rgba(240, 68, 56, 0.08);
}

.cycle-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  background: #fef3f2;
  color: #b42318;
  font-size: var(--text-xs2);
  font-weight: 600;
  white-space: nowrap;
}

.rule-card__header,
.rule-card__header-main,
.card-actions,
.card-actions-wrapper,
.feature-title,
.section-heading {
  display: flex;
  align-items: center;
}

.rule-card__header {
  padding: 14px 20px;
  border-bottom: 1px solid #eef1f5;
  display: block;
}

.rule-card__header--error {
  box-shadow: inset 0 0 0 2px #f04438;
}

.rule-card__header-main {
  justify-content: space-between;
  gap: 16px;
  min-height: 56px;
}

.feature-title {
  min-width: 0;
  gap: 10px;
  color: #667085;
  font-size: var(--text-xs1);
  flex: 1 1 auto;
}

.feature-icon {
  display: grid;
  place-items: center;
  width: 30px;
  height: 30px;
  border: 1px solid var(--accent-color);
  border-radius: 6px;
  background: var(--accent-soft);
}

.eyebrow,
.dash,
.feature-pill,
.name-pill,
.status,
.between-operator,
.preview-operator {
  font-weight: 500;
}

.eyebrow {
  color: #98a2b3;
  letter-spacing: 0.6px;
}

.dash {
  color: #344054;
}

.feature-pill {
  border-radius: 4px;
  background: var(--accent-soft);
  color: var(--accent-color);
  padding: 3px 7px;
  font-size: var(--text-xs1);
}

.name-pill {
  border-radius: 999px;
  background: #f2f4f7;
  color: #475467;
  padding: 4px 10px;
  font-size: var(--text-xs1);
  line-height: 1.2;
}

.rule-name-input {
  min-width: 180px;
  max-width: 240px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #d0d5dd;
  border-radius: 999px;
  background: #ffffff;
  color: #101828;
  font-size: var(--text-xs1);
  outline: none;
}

.rule-name-input:focus {
  border-color: var(--accent-color);
  box-shadow: 0 0 0 3px rgba(36, 107, 254, 0.12);
}

.rule-name-input--error,
.rule-name-input--error:focus {
  border-color: #f04438;
}

.rule-name-input::placeholder {
  color: #98a2b3;
}

.summary {
  color: #7a8699;
}

.card-actions {
  gap: 5px;
}

.card-actions-wrapper {
  flex: 0 0 auto;
}

.status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #00a651;
  font-size: var(--text-xs1);
  cursor: pointer;
}

.status.disabled,
.icon-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.status.inactive {
  color: #98a2b3;
}

.status-dot {
  width: 18px;
  height: 11px;
  border: 2px solid currentColor;
  border-radius: 999px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 3px;
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: currentColor;
  }
}

.icon-btn {
  display: grid;
  place-items: center;
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #98a2b3;
  cursor: pointer;
}

.delete-action:hover {
  background: #fff1f0;
}

.delete-action:hover ::v-deep .delete-icon {
  color: #f04438;
}

.edit-action:hover {
  background: #f2f4f7;
}

.edit-close-action:hover {
  background: #fff1f0;
}

.edit-save-action:hover {
  background: #ecfdf3;
}

.chevron {
  color: #98a2b3;
}

.rule-card__body {
  padding: 20px;
}

.rule-card__body--readonly {
  pointer-events: none;
}

.rule-card__body--readonly ::v-deep .group-toggle-btn,
.rule-card__body--readonly .preview-toggle-control,
.rule-card__body--readonly ::v-deep .condition-list {
  pointer-events: auto;
}

.rule-card__body--readonly ::v-deep .condition-list {
  overflow-y: auto;
}

.rule-card__body--readonly ::v-deep .condition-list > * {
  pointer-events: none;
}

.rule-action-btn {
  min-height: 30px;
  padding: 0 12px;
  font-size: var(--text-xs2);
}

.rule-section {
  padding: 18px 0;
  border-top: 1px solid #edf1f5;

  &:first-child {
    padding-top: 0;
    border-top: 0;
  }

  &:last-child {
    padding-bottom: 0;
    border-bottom: 0;
  }
}

.mandatory-checks{
   display: flex;
   flex-direction: column;
   gap: 8px;
  }

.rule-card__header-error-row {
  width: 100%;
}

.rule-card__error-message {
  margin: 12px 0 0;
  color: #dc2626;
  font-size: var(--text-xs2);
  font-weight: 500;
}

.rule-card__error-message--header {
  margin: 0;
  text-align: left;
}

.section-heading {
  gap: 8px;
  margin-block: 8px;

  h4 {
    margin: 0;
    color: #101828;
    font-size: var(--text-s);
    font-weight: 500;
    letter-spacing: 1px;

    span {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 17px;
      height: 16px;
      margin-left: 5px;
      border-radius: 4px;
      background: #eef2f7;
      color: #7a8699;
      font-size: var(--text-xs1);
      letter-spacing: 0;
    }
  }

  p {
    margin: 3px 0 0;
    color: #8a96aa;
    font-size: var(--text-xs2);
  }
}

.configure-section .section-heading {
  flex-wrap: nowrap;

  h4 {
    white-space: nowrap;
  }
}

.configure-dropdown {
  flex: 0 0 230px;
  width: 230px;
}

.section-heading.split {
  justify-content: space-between;
}

.section-heading.split {
  width: 100%;
}

.section-heading-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-symbol {
  display: inline-grid;
  place-items: center;
  width: 18px;
  height: 18px;
  color: #c9852f;
  font-size: var(--text-l);
}

.then-symbol {
  color: #12b76a;
  align-self: baseline;
  font-weight: 500;
}

.condition-set {
  margin-top: 16px;
  border: 1px solid #dfe5ed;
  border-radius: 8px;
  background: #ffffff;
}

.condition-set__body {
  padding: 0 16px 16px;
}

.conditions-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 224px;
  gap: 20px;
  transition: grid-template-columns 0.3s ease, gap 0.3s ease;
}

.conditions-layout--preview-collapsed {
  grid-template-columns: minmax(0, 1fr) 32px;
  gap: 10px;
}

.conditions-list {
  min-width: 0;
}

.empty-state {
  display: grid;
  place-items: center;
  width: 100%;
  border: 1px dashed #d7dde7;
  border-radius: 4px;
  color: #98a2b3;
  font-size: var(--text-xs1);
  text-align: center;
}

.empty-conditions {
  min-height: 80px;
  margin-top: 8px;
}

.empty-actions {
  min-height: 56px;
  margin: 12px 0 10px;
}

.empty-actions--group {
  min-height: 48px;
  margin: 8px 0 0;
}

.action-list {
  max-width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  scrollbar-gutter: stable;
}

.action-list ::v-deep .then-row {
  min-width: 560px;
}

.action-group {
  padding: 14px 16px;
  border: 1px solid #edf1f5;
  border-radius: 8px;
  background: #fcfdff;
}

.action-group + .action-group {
  margin-top: 12px;
}

.action-group__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}

.section-heading-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  flex-wrap: wrap;
}

.action-group__header h5 {
  margin: 0;
  color: #101828;
  font-size: var(--text-s);
  font-weight: 600;
}

.action-group__header p {
  margin: 3px 0 0;
  color: #8a96aa;
  font-size: var(--text-xs2);
}

.if-heading-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.if-heading-row .section-heading {
  margin: 8px 0 14px 0;
}

.between-operator-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  margin: -2px 0 12px;
  overflow: hidden;
  width: 100%;
}

.between-operator {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 38px;
  height: 24px;
  padding: 0 8px;
  border: 1px solid var(--operator-color);
  border-radius: 5px;
  background: var(--operator-soft);
  color: var(--operator-color);
  font-size: var(--text-xs2);
  cursor: pointer;
  z-index: 1;
  isolation: isolate;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 100vw;
    height: 1px;
    background: var(--operator-line);
    transform: translateY(-50%);
    z-index: -1;
    pointer-events: none;
  }

  &::before {
    right: calc(100% + 10px);
  }

  &::after {
    left: calc(100% + 10px);
  }
}

.operator-and {
  --operator-color: #246bfe;
  --operator-soft: #eef4ff;
  --operator-line: #c9ddff;
}

.operator-or {
  --operator-color: #f79009;
  --operator-soft: #fff8e7;
  --operator-line: #ffd38a;
}

.rule-preview {
  min-width: 0;
  padding-left: 18px;
  border-left: 1px solid #edf1f5;
  overflow: hidden;
  transition: padding-left 0.3s ease, border-color 0.3s ease;
}

.rule-preview--collapsed {
  display: flex;
  justify-content: center;
  width: 32px;
  min-width: 32px;
  padding-left: 0;
  border-left-color: #edf1f5;
}

.preview-title {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  margin-top: 8px;
  margin-bottom: 10px;
  color: #344054;
  font-size: var(--text-s);
  font-weight: 500;
  cursor: pointer;
}

.preview-toggle-icon {
  cursor: pointer;
  flex: 0 0 auto;
}

.collapsed-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 32px;
  padding-top: 10px;
}

.preview-divider {
  width: 20px;
  height: 1px;
  background: #ebe6e7;
}

.preview-vertical-text {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 96px;
  white-space: nowrap;

  span {
    color: #99a1af;
    font-size: var(--text-xs1);
    font-weight: 700;
    letter-spacing: 1px;
    transform: rotate(-90deg);
    transform-origin: center;
  }
}

.empty-preview {
  margin: 0;
  padding: 0 0 12px;
  border-bottom: 1px solid #dfe5ed;
  color: #98a2b3;
  font-size: var(--text-xs2);
  font-style: italic;
}

.preview-group {
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid var(--preview-color);
  border-left: 3px solid var(--preview-color);
  border-radius: 4px;
  background: var(--preview-soft);

  strong {
    display: block;
    margin-bottom: 8px;
    color: var(--preview-color);
    font-size: var(--text-xs2);
    font-weight: 600;
  }

  span {
    font-weight: 400;
  }

  p {
    margin: 0 0 6px;
    color: #344054;
    font-size: var(--text-xs2);
    line-height: 1.35;

    &::before {
      content: '-';
      margin-right: 6px;
      color: var(--preview-color);
    }
  }
}

.preview-operator-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 8px;
  width: 100%;
  margin: -4px auto 8px;
  color: var(--operator-color);
  font-size: var(--text-xs2);

  &::before,
  &::after {
    content: '';
    height: 1px;
    background: var(--operator-line);
  }
}

.preview-operator {
  display: grid;
  place-items: center;
  min-width: 38px;
  height: 22px;
  padding: 0 8px;
  border: 1px solid var(--operator-color);
  border-radius: 4px;
  background: var(--operator-soft);
  color: var(--operator-color);
  font-size: var(--text-xs2);
  z-index: 1;
}

.preview-1 {
  --preview-color: #2f80ff;
  --preview-soft: #eef6ff;
}

.preview-2 {
  --preview-color: #b45cff;
  --preview-soft: #fbf4ff;
}

.preview-3 {
  --preview-color: #f79009;
  --preview-soft: #fff9e7;
}

.preview-4 {
  --preview-color: #12b76a;
  --preview-soft: #ecfdf3;
}

.preview-5 {
  --preview-color: #e11d48;
  --preview-soft: #fff1f3;
}

.preview-6 {
  --preview-color: #0891b2;
  --preview-soft: #ecfeff;
}

.text-btn {
  border: 0;
  background: transparent;
  color: #246bfe;
  font-size: var(--text-s);
  font-weight: 500;
  cursor: pointer;
}

.text-btn.add-group-btn {
  color: #d9822b;
}

.text-btn.green {
  color: #00a651;
}

.text-btn--disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.inline-error {
  color: #dc2626;
  font-size: var(--text-xs2);
  font-weight: 500;
}

.configuration-confirmation {
  width: 360px;
  max-width: 100%;
}

.configuration-confirmation__message {
  margin: 0;
  color: #344054;
  font-size: var(--text-s);
  line-height: 1.5;
}

.configuration-confirmation__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.mandatory-checks-container {
  border: 1px solid #dfdede;
  border-radius: 10px;
  width: 100%;
  overflow: hidden;
}

.btn-secondary {
  background: #eef2f6;
  color: #344054;
}

.add-mandatory-checks {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #2e9d55;
  font-size: var(--text-s);
  font-weight: 500;
  cursor: pointer;
  user-select: none;
  transition: color 0.2s ease;

  &:hover {
    color: #248246;
  }

  .icon {
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
  }
}

::v-deep .custom-dropdown-container,
::v-deep .custom-select {
  width: 100%;
  max-width: 100%;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}
.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter, .slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

@media (max-width: 900px) {
  .rule-card__header {
    align-items: flex-start;
    flex-direction: column;
  }

  .conditions-layout {
    grid-template-columns: 1fr;
  }

  .conditions-layout--preview-collapsed {
    grid-template-columns: 1fr;
  }

  .rule-preview {
    padding-left: 0;
    border-left: 0;
  }

  .rule-preview--collapsed {
    justify-content: flex-start;
    width: 100%;
    min-width: 0;
    border-top: 1px solid #edf1f5;
  }

  .collapsed-preview {
    flex-direction: row;
    width: 100%;
    padding-top: 8px;
  }

  .preview-vertical-text {
    height: auto;

    span {
      transform: none;
    }
  }

  .action-list ::v-deep .then-row {
    min-width: 0;
  }

}
</style>
